/**
 * Redux slice for chatbot state management
 *
 * Manages chat widget visibility, session, and messages
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../store';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: Array<{
    title: string;
    source_path: string;
    source_type: string;
    distance: number;
  }>;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface ChatSession {
  id: string;
  language: string;
  started_at: string;
  last_activity_at: string;
  is_active: boolean;
  message_count: number;
}

interface ChatbotState {
  isOpen: boolean;
  session: ChatSession | null;
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
}

const initialState: ChatbotState = {
  isOpen: false,
  session: null,
  messages: [],
  isLoading: false,
  error: null,
};

// Load session from localStorage if available
const loadSessionFromStorage = (): ChatSession | null => {
  try {
    const stored = localStorage.getItem('chatbot_session');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load chat session from storage:', error);
  }
  return null;
};

// Save session to localStorage
const saveSessionToStorage = (session: ChatSession | null) => {
  try {
    if (session) {
      localStorage.setItem('chatbot_session', JSON.stringify(session));
    } else {
      localStorage.removeItem('chatbot_session');
    }
  } catch (error) {
    console.error('Failed to save chat session to storage:', error);
  }
};

const chatbotSlice = createSlice({
  name: 'chatbot',
  initialState: {
    ...initialState,
    session: loadSessionFromStorage(),
  },
  /* eslint-disable no-param-reassign */
  reducers: {
    openChat: state => {
      state.isOpen = true;
    },
    closeChat: state => {
      state.isOpen = false;
    },
    toggleChat: state => {
      state.isOpen = !state.isOpen;
    },
    setSession: (state, action: PayloadAction<ChatSession>) => {
      state.session = action.payload;
      saveSessionToStorage(action.payload);
    },
    clearSession: state => {
      state.session = null;
      state.messages = [];
      saveSessionToStorage(null);
    },
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },
    setMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      state.messages = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  /* eslint-enable no-param-reassign */
});

export const {
  openChat,
  closeChat,
  toggleChat,
  setSession,
  clearSession,
  addMessage,
  setMessages,
  setLoading,
  setError,
} = chatbotSlice.actions;

// Selectors
export const selectChatbotOpen = (state: RootState) => state.chatbot.isOpen;
export const selectChatSession = (state: RootState) => state.chatbot.session;
export const selectChatMessages = (state: RootState) => state.chatbot.messages;
export const selectChatLoading = (state: RootState) => state.chatbot.isLoading;
export const selectChatError = (state: RootState) => state.chatbot.error;

export default chatbotSlice.reducer;
